import { useEffect, useMemo, useState, useCallback, useRef, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useAuthStore } from './stores/auth.store';
import { useNotificationStore } from './stores/notification.store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import oneSignalService from './services/onesignal.service';
import { initAnalytics } from './helpers/analytics.helper';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Organizations from './pages/Organizations';
import Layout from './components/Layout';
import AppI18nProvider from './components/AppI18nProvider';
import { appI18n, getLanguageDir } from './i18n/app';
import { createAppTheme } from './theme';
import './i18n/app';
import Reports from './pages/esg/Reports';
import WasteManagement from './pages/esg/WasteManagement';
import DataEntry from './pages/esg/DataEntry';
import EmployeeTrainingOverview from './pages/esg/EmployeeTrainingOverview';
import EmployeeTrainingDetail from './pages/esg/EmployeeTrainingDetail';
import HseWeeklyReport from './pages/esg/HseWeeklyReport';
import HelpI18nProvider from './components/help/HelpI18nProvider';
import KnowledgeBase from './pages/support/KnowledgeBase';
import KnowledgeBaseArticle from './pages/support/KnowledgeBaseArticle';
import Notifications from './pages/Notifications';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import './i18n/help';

const queryClient = new QueryClient();

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({
  key: 'mui-rtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

function App() {
  const { isAuthenticated, initAuth } = useAuthStore();
  const oneSignalInitialized = useRef(false);
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (
      (savedMode as 'light' | 'dark') ||
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    );
  });

  const [direction, setDirection] = useState<'ltr' | 'rtl'>(() => getLanguageDir());

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setDirection(getLanguageDir(lng));
    appI18n.on('languageChanged', onLanguageChanged);
    return () => {
      appI18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const theme = useMemo(() => createAppTheme(mode, direction), [mode, direction]);

  useEffect(() => {
    initAnalytics();
    initAuth();

    if (!oneSignalInitialized.current) {
      oneSignalInitialized.current = true;
      oneSignalService.initialize();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const setupHandler = async () => {
        if (!oneSignalService.isReady()) {
          setTimeout(setupHandler, 1000);
          return;
        }

        const handleNotificationClick = async (notification: any) => {
          const notificationId = notification.id || notification.notificationId;

          const savedOrg = sessionStorage.getItem('selectedOrganization');
          let organizationId: string | null = null;

          if (savedOrg) {
            try {
              const parsedOrg = JSON.parse(savedOrg);
              organizationId = parsedOrg.id;
            } catch (e) {
              console.error('Error parsing saved organization:', e);
            }
          }

          if (organizationId && notificationId) {
            try {
              await useNotificationStore.getState().markAsRead(organizationId, notificationId);
            } catch (error) {
              console.error('Error marking notification as read:', error);
            }
          }
        };

        oneSignalService.setCustomClickHandler(handleNotificationClick);
      };

      setupHandler();
    }
  }, [isAuthenticated]);

  const authed = (page: ReactNode) =>
    isAuthenticated ? (
      <Layout onThemeToggle={toggleTheme}>{page}</Layout>
    ) : (
      <Navigate to="/login" />
    );

  return (
    <QueryClientProvider client={queryClient}>
      <AppI18nProvider>
        <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <CssBaseline />
              <Router>
                <Routes>
                  <Route
                    path="/login"
                    element={!isAuthenticated ? <Login /> : <Navigate to="/" />}
                  />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  <Route path="/" element={authed(<Dashboard />)} />
                  <Route path="/esg-reports" element={authed(<Reports />)} />
                  <Route path="/waste-management" element={authed(<WasteManagement />)} />
                  <Route path="/data-entry" element={authed(<DataEntry />)} />
                  <Route
                    path="/employee-training"
                    element={authed(<EmployeeTrainingOverview />)}
                  />
                  <Route
                    path="/employee-training/:userId"
                    element={authed(<EmployeeTrainingDetail />)}
                  />
                  <Route path="/hse" element={authed(<HseWeeklyReport />)} />
                  <Route path="/organizations" element={authed(<Organizations />)} />
                  <Route path="/users" element={<Navigate to="/" replace />} />
                  <Route path="/notifications" element={authed(<Notifications />)} />
                  <Route
                    path="/knowledge-base"
                    element={authed(
                      <HelpI18nProvider>
                        <KnowledgeBase />
                      </HelpI18nProvider>,
                    )}
                  />
                  <Route
                    path="/knowledge-base/:articleId"
                    element={authed(
                      <HelpI18nProvider>
                        <KnowledgeBaseArticle />
                      </HelpI18nProvider>,
                    )}
                  />
                </Routes>
              </Router>
            </LocalizationProvider>
          </ThemeProvider>
        </CacheProvider>
      </AppI18nProvider>
    </QueryClientProvider>
  );
}

export default App;
